import { Button, TextInput } from "client/Components";
import { SendOrSaveEmailDraftModel } from "client/Api/Email/respondToEmail";
import { IInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { isNotNilOrEmpty, setTrackingId } from "client/Misc/utils";
import { EmailAddressModel } from "client/Api/Email/email";
import { localize } from "client_legacy/ndaw/culture";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import { FromAddressSelector } from "./FromAddressSelector";
import { RecipientSelector, RecipientSelectorHandle } from "./RecipientSelector";
import styles from "./Header.module.less";
import { Recipients, useSelectRecipientsDialog } from "./SelectRecipientsDialog";

export type HeaderHandle = {
	focusToField(): void;
};

export type HeaderProps = {
	attachable: IValueInputAttachable<SendOrSaveEmailDraftModel>;
	fromAddressOptions: readonly EmailAddressModel[];
};

export const Header = forwardRef(
	(props: HeaderProps, ref: Ref<HeaderHandle>): ReactElement => {
		const {
			attachable,
			fromAddressOptions
		} = props;

		const toRef: RefObject<RecipientSelectorHandle | null> = useRef<RecipientSelectorHandle>(null);
		const [selectRecipientsDialog, openSelectRecipientsDialog] = useSelectRecipientsDialog();

		const [hasCopies, setHasCopies] = useState<boolean>(
			isNotNilOrEmpty(attachable.value.cc)
				|| isNotNilOrEmpty(attachable.value.bcc)
		);

		const openRecipientDialogHandler = useCallback(
			async (): Promise<void> => {
				const toAttachable: IInputAttachable<string> = attachable.for("to");
				const ccAttachable: IInputAttachable<string> = attachable.for("cc");
				const bccAttachable: IInputAttachable<string> = attachable.for("bcc");

				const result: TryResult<Recipients> = await openSelectRecipientsDialog({
					initialRecipients: {
						to: toAttachable.value,
						cc: ccAttachable.value,
						bcc: bccAttachable.value
					}
				});

				if (!result.wasSuccessful) {
					return;
				}

				const newRecipients: Recipients = result.result;
				toAttachable.reducer.setValue(newRecipients.to);
				toAttachable.validation.triggerInputFinished();

				ccAttachable.reducer.setValue(newRecipients.cc);
				ccAttachable.validation.triggerInputFinished();

				bccAttachable.reducer.setValue(newRecipients.bcc);
				bccAttachable.validation.triggerInputFinished();

				if (
					isNotNilOrEmpty(newRecipients.cc)
					|| isNotNilOrEmpty(newRecipients.bcc)
				) {
					setHasCopies(true);
				}
			},
			[attachable, openSelectRecipientsDialog]
		);

		useImperativeHandle(
			ref,
			(): HeaderHandle => ({
				focusToField(): void {
					toRef.current
						?.focusInput();
				},
			}),
			[]
		);

		return (
			<div
				{...setTrackingId("header")}
				className={styles.root}
			>
				<FromAddressSelector
					attachable={attachable.for("fromAddress")}
					options={fromAddressOptions}
				/>
				<div className={styles.toRow}>
					<RecipientSelector
						ref={toRef}
						label={localize("_To")}
						attachable={attachable.for("to")}
						onOpenRecipientDialog={openRecipientDialogHandler}
					/>
					{!hasCopies && (
						<div>
							<Button
								text={localize("_AddCcBcc")}
								onClick={() => setHasCopies(true)}
								kind="transparent"
								icon="plus"
								trackingId="add-cc-bcc-button"
							/>
						</div>
					)}
				</div>
				{hasCopies && (
					<>
						<div>
							<RecipientSelector
								label={localize("_Cc")}
								attachable={attachable.for("cc")}
								onOpenRecipientDialog={openRecipientDialogHandler}
							/>
						</div>
						<div>
							<RecipientSelector
								label={localize("_Bcc")}
								attachable={attachable.for("bcc")}
								onOpenRecipientDialog={openRecipientDialogHandler}
							/>
						</div>
					</>
				)}
				<div>
					<TextInput
						{...attachable.for("subject")
							.attachToTextInput()
						}
						placeholder={localize("_Subject")}
					/>
				</div>
				{selectRecipientsDialog}
			</div>
		);
	}
);
Header.displayName = "Header";